// src/services/authService.js
import { 
    createUserWithEmailAndPassword, 
    signInWithEmailAndPassword, 
    signOut,
    sendPasswordResetEmail
  } from 'firebase/auth';
  import { doc, setDoc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
  import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
  import { auth, db, storage } from './firebase';
  
  /**
   * Register a new vendor
   * @param {Object} vendorData - Vendor registration data
   * @returns {Promise} - Firebase auth user
   */
  export const registerVendor = async (vendorData) => {
    try {
      // Create user with email and password
      const userCredential = await createUserWithEmailAndPassword(
        auth, 
        vendorData.email, 
        vendorData.password
      );
      
      const user = userCredential.user;
      
      // Upload shop logo if provided
      let logoURL = null;
      if (vendorData.logo) {
        const logoRef = ref(storage, `vendor_logos/${user.uid}`);
        await uploadBytes(logoRef, vendorData.logo);
        logoURL = await getDownloadURL(logoRef);
      }
      
      // Create vendor profile in Firestore
      const vendorProfile = {
        uid: user.uid,
        shopName: vendorData.shopName,
        contactPersonName: vendorData.contactPersonName,
        phone: vendorData.phone,
        email: vendorData.email,
        businessType: vendorData.businessType || 'with-orders', // Add this line
        address: vendorData.address,
        location: vendorData.location, // GeoPoint with lat and long
        categories: vendorData.categories,
        brands: vendorData.brands,
        logoURL,
        createdAt: new Date(),
        updatedAt: new Date(),
        isActive: true,
        isVerified: false, // Will be verified by admin
        rating: 0,
        totalOrders: 0,
        totalRevenue: 0,
        totalProducts: 0
      };
      
      await setDoc(doc(db, "vendors", user.uid), vendorProfile);
      
      return { user, profile: vendorProfile };
    } catch (error) {
      throw error;
    }
  };
  
  /**
   * Sign in a vendor
   * @param {string} email - Vendor email
   * @param {string} password - Vendor password
   * @returns {Promise} - Firebase auth user
   */
  export const loginVendor = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // Update last login timestamp
      const vendorRef = doc(db, "vendors", user.uid);
      await updateDoc(vendorRef, {
        lastLogin: new Date()
      });
      
      // Get vendor profile
      const vendorSnap = await getDoc(vendorRef);
      if (vendorSnap.exists()) {
        return { user, profile: vendorSnap.data() };
      } else {
        throw new Error("Vendor profile not found");
      }
    } catch (error) {
      throw error;
    }
  };
  
  /**
   * Sign out current vendor
   * @returns {Promise} - Void
   */
  export const logoutVendor = async () => {
    try {
      return await signOut(auth);
    } catch (error) {
      throw error;
    }
  };
  
  /**
   * Send password reset email
   * @param {string} email - Vendor email
   * @returns {Promise} - Void
   */
  export const resetPassword = async (email) => {
    try {
      return await sendPasswordResetEmail(auth, email);
    } catch (error) {
      throw error;
    }
  };
  
  /**
   * Get current vendor profile
   * @param {string} uid - Vendor uid
   * @returns {Promise} - Vendor profile
   */
  export const getVendorProfile = async (uid) => {
    try {
      const vendorRef = doc(db, "vendors", uid);
      const vendorSnap = await getDoc(vendorRef);
      
      if (vendorSnap.exists()) {
        return vendorSnap.data();
      } else {
        throw new Error("Vendor profile not found");
      }
    } catch (error) {
      throw error;
    }
  };
  
  /**
   * Update vendor profile
   * @param {string} uid - Vendor uid
   * @param {Object} profileData - Updated profile data
   * @returns {Promise} - Updated vendor profile
   */
  export const updateVendorProfile = async (uid, profileData) => {
    try {
      const vendorRef = doc(db, "vendors", uid);
      
      // Upload new logo if provided
      if (profileData.newLogo) {
        const logoRef = ref(storage, `vendor_logos/${uid}`);
        await uploadBytes(logoRef, profileData.newLogo);
        profileData.logoURL = await getDownloadURL(logoRef);
        delete profileData.newLogo; // Remove from data to be saved
      }
      
      // Update timestamp
      profileData.updatedAt = new Date();
      
      await updateDoc(vendorRef, profileData);
      
      // Get updated profile
      return await getVendorProfile(uid);
    } catch (error) {
      throw error;
    }
  };